import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { BackButton } from '../components/common/BackButton';
import { servicesService, getServiceImageUrl, MedicalService } from '../services/servicesService';
import { createAppointmentRequest } from '../services/requestsService';

// Стили для анимации
const imageAnimationStyle = `
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(100px) scale(0.95);
      filter: blur(8px);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0);
    }
  }

  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-100px) scale(0.95);
      filter: blur(8px);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .image-slide-right {
    animation: slideInFromRight 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .image-slide-left {
    animation: slideInFromLeft 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .fade-in-up {
    animation: fadeInUp 0.4s ease-out;
  }
`;

// Вставляем стили в head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = imageAnimationStyle;
  if (!document.head.querySelector('style[data-image-animation]')) {
    styleElement.setAttribute('data-image-animation', 'true');
    document.head.appendChild(styleElement);
  }
}

export const BeforeAfterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const serviceId = useMemo(() => searchParams.get('service'), [searchParams]);

  // Состояния
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [service, setService] = useState<MedicalService | null>(
    (location.state as any)?.service || null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
    open: false,
    message: '',
    type: 'success',
  });

  // Для свайпа
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const prevIndexRef = useRef(0);

  // ЗАГРУЗКА КОЛЛАЖА И ДАННЫХ УСЛУГИ
  useEffect(() => {
    const loadData = async () => {
      if (!serviceId) {
        setError('Услуга не найдена');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Загружаем коллаж
        const result = await servicesService.getServiceCollage(serviceId);
        const collageImages = result.images || [];

        if (collageImages.length === 0) {
          setError('Нет доступных изображений');
        } else {
          setImages(collageImages);
          setCurrentIndex(0);
        }

        // Если услуга не передана, загружаем её
        if (!service) {
          try {
            const serviceRes = await servicesService.getService(serviceId);
            setService(serviceRes.service);
          } catch (e) {
            console.error('Ошибка при загрузке услуги:', e);
          }
        }
      } catch (e) {
        console.error('Ошибка при загрузке коллажа:', e);
        setError('Ошибка загрузки изображений');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [serviceId, service]);

  // НАВИГАЦИЯ ВПЕРЁД
  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setDirection('right');
      prevIndexRef.current = currentIndex;
      setCurrentIndex(currentIndex + 1);
    }
  };

  // НАВИГАЦИЯ НАЗАД
  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection('left');
      prevIndexRef.current = currentIndex;
      setCurrentIndex(currentIndex - 1);
    }
  };

  // СВАЙП ВЛЕВО (вперёд)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
    handleSwipe();
  };

  const handleSwipe = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setDirection('right');
      prevIndexRef.current = currentIndex;
      if (currentIndex < images.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    } else if (isRightSwipe) {
      setDirection('left');
      prevIndexRef.current = currentIndex;
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  // ЗАПИСЬ НА УСЛУГУ
  const handleAppointment = async () => {
    if (!serviceId) return;
    setIsSubmitting(true);

    try {
      await createAppointmentRequest({
        serviceId,
      });

      setSnackbar({
        open: true,
        message: 'Спасибо! Мы свяжемся с вами в ближайшее время',
        type: 'success',
      });
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (e) {
      console.error('Ошибка при записи:', e);
      setSnackbar({
        open: true,
        message: 'Ошибка при записи на услугу',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!serviceId) {
    return (
      <AppLayout title="До/После">
        <Box sx={{ pb: 8, px: 1, textAlign: 'center', mt: 4 }}>
          <Typography color="error">Услуга не найдена</Typography>
        </Box>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout title="До/После">
        <Box sx={{ pb: 8, px: 1, display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error || images.length === 0) {
    return (
      <AppLayout title="До/После">
        <Box sx={{ pb: 8, px: 1 }}>
          <BackButton onClick={() => navigate(-1)} />
          <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>
            {error || 'Нет доступных изображений'}
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  const currentImage = images[currentIndex];
  const imageUrl = getServiceImageUrl(currentImage);

  return (
    <AppLayout title="До/После">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: 1,
        }}
      >
        {/* Контент (скроллируемый) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BackButton onClick={() => navigate(-1)} />

          {/* Слайдер с изображением */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            <Box
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                aspectRatio: '365 / 205',
                borderRadius: '16px',
                backgroundColor: '#E8E8E8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                px: { xs: 1, sm: 0 },
              }}
          >
            {imageUrl && (
              <img
                key={currentIndex}
                src={imageUrl}
                alt={`До/После ${currentIndex + 1}`}
                className={direction === 'right' ? 'image-slide-right' : 'image-slide-left'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#E8E8E8',
                }}
              />
            )}

            {/* Кнопка Назад (слева) */}
            {currentIndex > 0 && (
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: { xs: 4, sm: 8 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  color: '#007AFF',
                  zIndex: 10,
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 4px 12px rgba(0, 122, 255, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 6px 16px rgba(0, 122, 255, 0.3)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(0.95)',
                    boxShadow: '0 2px 8px rgba(0, 122, 255, 0.15)',
                  },
                }}
              >
                <ChevronLeft sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            )}

            {/* Кнопка Вперёд (справа) */}
            {currentIndex < images.length - 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: { xs: 4, sm: 8 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  color: '#007AFF',
                  zIndex: 10,
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 4px 12px rgba(0, 122, 255, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 6px 16px rgba(0, 122, 255, 0.3)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(0.95)',
                    boxShadow: '0 2px 8px rgba(0, 122, 255, 0.15)',
                  },
                }}
              >
                <ChevronRight sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            )}
            </Box>
          </Box>

          {/* Индикатор прогресса */}
          <Box
            className="fade-in-up"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              animationDelay: '0.1s',
              animation: 'fadeInUp 0.4s ease-out 0.1s both',
            }}
          >
            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: '#8E8E93', fontWeight: 500 }}>
              {currentIndex + 1} / {images.length}
            </Typography>
          </Box>

          {/* Точки-индикаторы */}
          {images.length > 1 && (
            <Box
              className="fade-in-up"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
                mb: 1.5,
                animation: 'fadeInUp 0.4s ease-out 0.15s both',
              }}
            >
              {images.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => {
                    setDirection(idx > currentIndex ? 'right' : 'left');
                    prevIndexRef.current = currentIndex;
                    setCurrentIndex(idx);
                  }}
                  sx={{
                    width: idx === currentIndex ? 20 : 6,
                    height: 6,
                    borderRadius: '3px',
                    backgroundColor: idx === currentIndex ? '#007AFF' : '#D0D0D0',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': {
                      backgroundColor: idx === currentIndex ? '#007AFF' : '#A0A0A0',
                      transform: 'scale(1.2)',
                    },
                    '&:active': { transform: 'scale(0.85)' },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Описание услуги в плитке */}
          {service && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: { xs: 2, sm: 3 },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                  backgroundColor: '#FFFFFF',
                  borderRadius: '24px',
                  px: { xs: 2, sm: 2.5, md: 3 },
                  py: { xs: 2, sm: 2.5 },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: '#212121', mb: 1, lineHeight: 1 }}>
                  {service.name}
                </Typography>
                {service.description && (
                  <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#212121', lineHeight: 1.2 }}>
                    {service.description}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Кнопка записи фиксирована к низу */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 96,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            pb: 1,
            backgroundColor: '#F4F5F7',
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleAppointment()}
            disabled={isSubmitting}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '480px', md: '600px' },
              height: { xs: 48, sm: 54, md: 58 },
              backgroundColor: '#007AFF',
              borderRadius: '22px',
              fontWeight: 600,
              fontSize: { xs: 14, sm: 16 },
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(0, 122, 255, 0.3)',
              '&:disabled': {
                backgroundColor: '#A9C7F5',
                boxShadow: 'none',
              },
              px: { xs: 1, sm: 0 },
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#FFF' }} /> : 'Записаться'}
          </Button>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
};
